// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { avoidBreakInsidePrintElement, color, xSafariOnly, xScreen } from "@adl/XDL/Utilities";
import { mergeWithCss } from "@adl/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Children, FC, useState } from "react";
import { css } from "styled-components";

export interface CartLineContainerStyles {
    container?: InjectableCss;
    descriptionContainer?: InjectableCss;
    image?: InjectableCss;
    infoSection?: InjectableCss;
    rightSection?: InjectableCss;
    qtyPriceSection?: InjectableCss;
    qty?: InjectableCss;
    price?: InjectableCss;
    fullPrice?: InjectableCss;
    total?: InjectableCss;
    peripheralActions?: InjectableCss;
    readOnlyNotes?: InjectableCss;
}

export const cartLineContainerStyles: CartLineContainerStyles = {
    container: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 20px 12px 18px;
            display: flex;
            flex-wrap: wrap;
            position: relative;
            width: 100%;
            ${avoidBreakInsidePrintElement}
            ${xScreen("lt", "md")} {
                padding: 20px 0;
            }
            &:hover {
                div[data-test-selector="product_removeLine"] > div {
                    visibility: visible;
                }
            }
            ${xSafariOnly(`@media print {
                height: 99%;
            }`)}
        `,
    },
    descriptionContainer: {
        css: css`
            display: flex;
            ${xScreen("lte", "380")} {
                flex-wrap: wrap;
            }
        `,
    },
    image: {
        css: css`
            width: 75px;
            margin-right: 22px;
            margin-bottom: 15px;
            @media print {
                max-width: 75px;
            }
            ${xScreen("lte", "380")} {
                width: 100%;
            }
        `,
    },
    infoSection: {
        css: css`
            width: 435px;
            padding-right: 40px;
            ${xScreen("lte", "md")} {
                width: calc(100% - 97px);
                padding-right: 0;
            }
            @media print {
                width: 90%; // fallback (calculate not work with pdf generation)
                width: calc(100% - 97px);
            }
        `,
    },
    readOnlyNotes: {
        css: css`
            max-width: 510px;
            width: 100%;
            ${xScreen("lte", "lg")} {
                margin-bottom: 10px;
            }
        `,
    },
    rightSection: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    qtyPriceSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 28px;
            ${xScreen("lte", "md")} {
                flex-wrap: wrap;
                margin-bottom: 15px;
            }
            @media print {
                margin-bottom: 0;
                display: block;
                & > div {
                    display: block;
                    float: left;
                    width: auto;
                    min-width: 100px;
                }
                & > div:first-child {
                    clear: both;
                    margin-left: 110px;
                }
                &:after {
                    clear: both;
                    display: block;
                    content: "";
                }
            }
        `,
    },
    qty: {
        css: css`
            display: flex;
            flex: 1;
            ${xScreen("lte", "md")} {
                order: 1;
            }
            @media print {
                div div {
                    display: flex;
                    align-items: center;

                    p {
                        margin: 0;
                        padding: 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        &:before {
                            margin-right: 4px;
                        }
                        &:first-child {
                            margin-right: 4px;
                        }
                    }
                }
            }
        `,
    },
    price: {
        css: css`
            display: flex;
            align-items: center;
            flex: 1;
            flex-wrap: wrap;
            legend {
                display: none;
            }
            span.tooltip-container {
                display: flex;
                align-items: center;
            }
            ${xScreen("lte", "md")} {
                order: 0;
                flex: 100%;
                margin-bottom: 12px;
                justify-content: space-between;
            }

            @media print {
                margin-bottom: 0;
                div div {
                    display: flex;
                    align-items: center;
                    p:first-child {
                        margin-right: 15px;
                    }
                }
            }
        `,
    },
    fullPrice: {
        css: css`
            display: flex;
            align-items: center;
            flex: 1;
            span.tooltip-container {
                display: flex;
                align-items: center;
            }
            ${xScreen("lte", "md")} {
                order: 0;
                flex: 100%;
            }
        `,
    },
    total: {
        css: css`
            flex: 1;
            display: flex;
            align-items: center;
            legend {
                display: none;
            }
            ${xScreen("lte", "md")} {
                order: 2;
                text-align: right;
            }

            @media print {
                div div {
                    display: flex;
                    align-items: center;
                    justify-content: end;
                    p:first-child {
                        margin-right: 15px;
                    }
                }
            }
        `,
    },
    peripheralActions: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            width: 100%;
            line-height: 17px;
            flex-wrap: wrap;
        `,
    },
};

const Image: FC = () => null;
const Info: FC = () => null;
const PeripheralActions: FC = () => null;
const Qty: FC = () => null;
const Price: FC = () => null;
const Total: FC = () => null;
const ReadOnlyNotes: FC = () => null;

const CartLineContainer = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: Array<React.ReactElement>;
    extendedStyles?: CartLineContainerStyles;
    [key: string]: any;
}) => {
    const [styles] = useState(() => mergeWithCss(cartLineContainerStyles, extendedStyles));

    const childrenArray = Children.toArray(children) as unknown as React.ReactElement[];
    const imageSlot = childrenArray.find(child => child.type === Image);
    const infoSlot = childrenArray.find(child => child.type === Info);
    const peripheralActions = childrenArray.find(child => child.type === PeripheralActions);
    const qtySlot = childrenArray.find(child => child.type === Qty);
    const priceSlot = childrenArray.find(child => child.type === Price);
    const totalSlot = childrenArray.find(child => child.type === Total);
    const readOnlynoteSlot = childrenArray.find(child => child.type === ReadOnlyNotes);

    return (
        <StyledWrapper {...styles.container} {...otherProps} className="cart-container-layout-container">
            <StyledWrapper>
                <StyledWrapper {...styles.descriptionContainer}>
                    <StyledWrapper {...styles.image} className="cart-container-layout-img">
                        {imageSlot?.props?.children}
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoSection} className="cart-container-layout-info">
                        {infoSlot?.props.children}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.readOnlyNotes} className="cart-container-layout-Note">
                    {readOnlynoteSlot?.props?.children}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection} className="cart-container-layout-righ-section">
                <StyledWrapper {...styles.qtyPriceSection} className="cart-container-layout-qty-price-section">
                    <StyledWrapper {...styles.qty}>{qtySlot?.props.children}</StyledWrapper>
                    <StyledWrapper {...(totalSlot?.props.children ? styles.price : styles.fullPrice)}>
                        {priceSlot?.props.children}
                    </StyledWrapper>
                    {totalSlot?.props.children && (
                        <StyledWrapper {...styles.total}>{totalSlot?.props.children}</StyledWrapper>
                    )}
                </StyledWrapper>
                {peripheralActions?.props?.children && (
                    <StyledWrapper {...styles.peripheralActions} className="cart-container-layout-peripheral-actions">
                        {peripheralActions?.props?.children}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

CartLineContainer.Image = Image;
CartLineContainer.Info = Info;
CartLineContainer.Qty = Qty;
CartLineContainer.Price = Price;
CartLineContainer.Total = Total;
CartLineContainer.PeripheralActions = PeripheralActions;
CartLineContainer.ReadOnlyNotes = ReadOnlyNotes;

export interface LineHeaderStyles {
    wrapper?: InjectableCss;
    itemDescription?: InjectableCss;
    rightSection?: InjectableCss;
    qty?: InjectableCss;
    priceUnit?: InjectableCss;
    total?: InjectableCss;
}

export const lineHeaderStyles: LineHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding: 7px 10px;
            background: ${color("common.ui9")};
            font-size: 15px;
            line-height: 26px;
            width: 100%;
        `,
    },
    itemDescription: {
        css: css`
            width: 535px;
            font-size: 15px;
            line-height: 26px;
        `,
    },
    rightSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            justify-content: space-between;
            font-size: 15px;
            line-height: 26px;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    qty: {
        css: css`
            flex: 1;
        `,
    },
    priceUnit: {
        css: css`
            flex: 1;
        `,
    },
    total: {
        css: css`
            flex: 1;
        `,
    },
};

export const LinesHeader: FC<{ leftTitle?: string; rightTitles?: string[]; extendedStyles?: LineHeaderStyles }> = ({
    leftTitle,
    rightTitles,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeWithCss(lineHeaderStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.itemDescription}>{translate(leftTitle || "")}</StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <StyledWrapper {...styles.qty}>{translate(rightTitles?.[0] || "")}</StyledWrapper>
                <StyledWrapper {...styles.priceUnit}>{translate(rightTitles?.[1] || "")}</StyledWrapper>
                {rightTitles?.[2] && (
                    <StyledWrapper {...styles.total}>{translate(rightTitles?.[2] || "")}</StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

LinesHeader.defaultProps = {
    leftTitle: "Item Description",
    rightTitles: ["QTY", "Price/Unit", "Total"],
};

export default CartLineContainer;
